<i18n src="./resources/locales.json"></i18n>

<template>
  <v-card
    :color="isHighContrastThemeActive ? 'surface' : 'surface lighten-1'"
    :class="{ 'elevation-0': plain }"
  >
    <v-list-item>
      <v-list-item-avatar>
        <SmsAvatar :contact="contact" :size="36" />
      </v-list-item-avatar>

      <v-list-item-content
        class="sms-contact-card-name"
        :class="{ 'mobile-container' : $vuetify.breakpoint.mobile }"
      >
        <v-list-item-title>{{ contactDisplayName }} </v-list-item-title>
      </v-list-item-content>

      <v-list-item-action v-if="hideContactActions">
        <v-btn icon @click="addToContacts">
          <v-icon>
            mdi-account-plus
          </v-icon>
        </v-btn>
      </v-list-item-action>
      <v-list-item-action v-if="!hideContactActions && !isUnknownContact">
        <v-btn icon @click="share">
          <v-icon>
            mdi-share-variant
          </v-icon>
        </v-btn>
      </v-list-item-action>
      <v-list-item-action v-if="!hideContactActions && !isUnknownContact && !contact.isCurrentAgentContact">
        <SmsFavoriteContact :contact="contact" />
      </v-list-item-action>
    </v-list-item>

    <v-divider />

    <v-list-item v-if="isUnknownContact && !this.bestConversation.isGroupConversation"
      :color="isHighContrastThemeActive ? 'surface' : 'surface lighten-1'"
    >
      <v-list-item-content>
        <v-btn class="sms-primary-button" @click="addToContacts">
          {{ $t('addToContacts') }}
        </v-btn>
      </v-list-item-content>
    </v-list-item>

    <v-list
      :color="isHighContrastThemeActive ? 'surface' : 'surface lighten-1'"
      dense
    >
      <!-- Phone Number (Type) -->
      <v-list-item v-if="contact.number" dense>
        <v-list-item-icon>
          <v-icon>
            mdi-phone
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('phoneNumber') }}
          </v-list-item-title>
          <v-list-item-subtitle> {{ contact.number }} {{ contactTypeText }} </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <!-- Company -->
      <v-list-item v-if="contact.company" dense>
        <v-list-item-icon>
          <v-icon>
            mdi-domain
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('company') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ contact.company }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <template v-if="quickMessage">
      <v-divider />

      <v-list-item class="pr-2">
        <v-list-item-content>
          <SmsTextFieldComponent
            ref="messageInput"
            quick-message
            :disabled="disabled"
            :message-input-label="quickMessagePlaceholder"
            @sendMessage="sendQuickMessage"
          />
        </v-list-item-content>
        <v-list-item-action class="ma-0 ml-2">
          <v-btn icon :disabled="disabled" @click="sendQuickMessage">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </template>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SmsAvatar from '@/components/SmsAvatar/SmsAvatar';
import SmsFavoriteContact from '@/components/SmsFavoriteContact/SmsFavoriteContact';
import SmsTextFieldComponent from '@/components/SmsConversation/SmsFooterComponents/SmsTextFieldComponent';
import Message from '@/classes/v2/Message';
import ToastMixin from '@/mixins/ToastMixin';
import ThemeMixin from '@/mixins/ThemeMixin';
import { TAB_NAME } from '@/constants/tabNameConstants';

export default {
  name: 'SmsContactCard',

  mixins: [ ToastMixin, ThemeMixin ],

  components: { SmsTextFieldComponent, SmsAvatar, SmsFavoriteContact },

  data() {
    return {
      tabName: { ...TAB_NAME }
    };
  },
  props: {
    quickMessage: {
      type: Boolean,
      default: false
    },
    plain: {
      type: Boolean,
      default: false
    },
    contact: {
      type: Object,
      default: null
    },
    hideContactActions: {
      type: Boolean,
      default: false
    },
    conversation: {
      type: Object
    }
  },

  computed: {
    ...mapGetters('user', ['currentUser', 'activeNumber']),
    ...mapGetters('conversationsV2', ['activeConversation']),
    ...mapGetters('preferences', ['currentTab']),

    disabled() {
      return this.activeNumber?.isBlocked(this.contact.number);
    },

    quickMessagePlaceholder() {
      return this.disabled
        ? this.$t('disabledQuickMsg', { name: this.contact.displayName })
        : this.$t('quickMessagePlaceholder', { name: this.contact.displayName });
    },

    isUnknownContact() {
      return this.contact.fullName === '';
    },

    contactTypeText() {
      return this.contact.type ? `(${this.$t(this.contact.type.toLowerCase())})` : '';
    },

    bestConversation() {
      // Use a supplied conversation by default.  Otherwise use the active conversation!
      if (this.conversation) return this.conversation;

      return this.activeConversation;
    },

    contactDisplayName() {
      if (!this.bestConversation || !this.bestConversation?.isGroupConversation || this.currentTab === this.tabName.settings) {
        return this.contact.displayName;
      }

      const displayNames = [];
      this.bestConversation.contacts.forEach(contact => {
        displayNames.push(contact.displayName);
      });

      return displayNames.join(",");
    },
  },

  methods: {
    ...mapActions('conversationsV2', ['sendSMS']),

    share() {
      this.$root.$emit('share-contacts', [this.contact]);
    },

    addToContacts() {
      this.$root.$emit('open-contact-dialog', this.contact);
    },

    sendQuickMessage() {
      if (!this.$refs.messageInput.messageText) return;

      this.sendSMS(
        new Message({
          from: this.currentUser.number,
          to: this.contact.number,
          body: this.$refs.messageInput.messageText,
          originationTime: Date.now()
        })
      ).then(() => {
        this.showSuccess(this.$t('messageSent', { name: this.contact.displayName }));
      });

      this.clearMessageInput();
      this.$emit('message-sent');
    },

    clearMessageInput() {
      this.$refs.messageInput.messageText = '';
    }
  }
};
</script>

<style scoped>
.sms-contact-card-name.mobile-container {
  max-width: 120px;
}
</style>
